/**
 * @voltxampere/web — Onboarding tour boot wiring (Sprint 23 C2)
 *
 * IDB-aware glue between `tour-state.ts` (pure signals) and the
 * persistence settings store. Called once from `main.ts` after
 * `initRecentCommandIds`.
 *
 *   initTour():
 *     1. getSettings() → check tour.shown
 *     2. configureTourPersister(idb-mergeSettings adapter)
 *     3. tourShownPersisted = settings.tour?.shown ?? false
 *     4. if !tour.shown → startTour() on next tick (DOM stabil olmasını
 *        bekle — hash-router pane mount + boot fixture render)
 *
 * Reset path:
 *   AYARLAR "Tour'u tekrar göster" → reArmTour() → persister yazar,
 *   tour açılır.
 *
 * IDB hata toleranslı: getSettings throw atarsa tour açılmaz; bir
 * sonraki boot tekrar dener.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { getSettings, mergeSettings } from "@voltxampere/persistence";

import { configureTourPersister, startTour, tourShownPersisted } from "./tour-state.js";

/** True once `initTour()` has run successfully. Idempotent guard. */
let initialised = false;

/**
 * v1.0.2 hotfix (ADR-0106, KOL-C-TOUR-AGGRESSIVE test reliability):
 * Sprint 64-69 audit Playwright runs raced against this function's
 * IDB read — the test's `addInitScript` seed of `tour.shown=true`
 * is asynchronous (the IDB transaction may not commit before
 * `getSettings()` queries). Without a synchronous override path,
 * audit specs frequently saw the tour open despite the seed.
 *
 * Pre-boot tests now set `window.__VXA_FORCE_TOUR_SHOWN = true` via
 * `addInitScript` (runs synchronously before any module code) and
 * `initTour()` honors it as a definitive "skip" signal. Real-user
 * paths are unaffected — the hook only fires when the flag is
 * explicitly set, which never happens in production builds.
 */
declare global {
  interface Window {
    __VXA_FORCE_TOUR_SHOWN?: boolean;
  }
}

export async function initTour(): Promise<void> {
  if (initialised) return;
  initialised = true;

  // Synchronous override — purely test-environment, ignored in
  // production since no app code ever sets the flag.
  if (typeof window !== "undefined" && window.__VXA_FORCE_TOUR_SHOWN === true) {
    tourShownPersisted.value = true;
    return;
  }

  // Wire IDB persister upfront so `finishTour` saves even if the
  // initial fetch below fails (we may still want to mark a future
  // user-driven completion).
  configureTourPersister(async (shown, lastStep) => {
    await mergeSettings({ tour: { shown, lastStep } });
  });

  let shown = false;
  try {
    const settings = await getSettings();
    shown = settings?.tour?.shown ?? false;
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn("[VXA tour] getSettings failed at boot:", err);
    }
    // Hata varsa: muhafazakâr — tour'u açma. Kullanıcı AYARLAR
    // > "Tour'u tekrar göster"'den manuel tetikleyebilir.
    return;
  }

  tourShownPersisted.value = shown;

  if (!shown) {
    // Hash-router pane mount + boot fixture render için bir
    // microtask bekle. requestAnimationFrame ile bir sonraki paint
    // sonrası başlat — spotlight target query selector'ları DOM'da
    // hazır olur.
    requestAnimationFrame(() => {
      startTour();
    });
  }
}
